import React, { useState } from 'react';
import { Plus, Search, Edit, Trash2, Loader2, Calendar, User, FileText, Eye, Mail, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { tareasDespachoAPI } from '../../api';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import ExpedienteSearch from '../ExpedienteSearch';
import MinutaDetailDialog from './MinutaDetailDialog';
import { FASES_MINUTAS } from './constants';
import { formatDate } from './utils';

const TareaMinutasTab = ({ tareas, loading, search, setSearch, filterCompletada, setFilterCompletada, filterPrioridad, setFilterPrioridad, loadTareas, handleDelete, usuarios, config }) => {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedMinuta, setSelectedMinuta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterFase, setFilterFase] = useState('');
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [form, setForm] = useState({
    descripcion_tarea: '',
    fecha_limite_fase_a: '',
    asignado_a_id: '',
    asignado_a_nombre: ''
  });

  const resetForm = () => {
    setForm({
      descripcion_tarea: '',
      fecha_limite_fase_a: '',
      asignado_a_id: '',
      asignado_a_nombre: ''
    });
    setSelectedExpediente(null);
  };

  const handleUsuarioChange = (userId) => {
    const user = usuarios.find(u => u.id.toString() === userId);
    setForm({
      ...form,
      asignado_a_id: userId,
      asignado_a_nombre: user?.nombre || user?.email || ''
    });
  };

  // Funciones para archivos
  const loadArchivosAbogados = async (tareaId) => {
    try {
      const res = await tareasDespachoAPI.abogados.getArchivos(tareaId);
      setArchivosAbogados(res.data || res || []);
    } catch (e) { setArchivosAbogados([]); }
  };

  const handleFileChangeAbogados = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) setPendingFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const handleRemovePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteArchivoAbogados = async (archivoId) => {
    if (!confirm('¿Eliminar archivo?')) return;
    try {
      await tareasDespachoAPI.abogados.deleteArchivo(archivoId);
      toast({ title: 'Archivo eliminado' });
      if (editingTarea) loadArchivosAbogados(editingTarea.id);
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSaving(true);
    try {
      const data = {
        ...form,
        expediente_id: selectedExpediente?.id || null,
        expediente_numero: selectedExpediente?.numero_expediente || null,
        asignado_a_id: form.asignado_a_id ? parseInt(form.asignado_a_id) : null,
        fecha_limite_fase_a: form.fecha_limite_fase_a || null
      };
      
      await tareasDespachoAPI.minutas.create(data);
      toast({ title: 'Minuta creada en Fase A' });
      
      setIsDialogOpen(false);
      resetForm();
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo crear', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetail = (minuta) => {
    setSelectedMinuta(minuta);
    setIsDetailOpen(true);
  };

  const handleAvanzarFase = async (minuta, fase, formData = {}) => {
    try {
      if (fase === 'fase_a') {
        await tareasDespachoAPI.minutas.updateFaseA(minuta.id, { fase_a_completada: true });
        toast({ title: 'Fase A completada, avanzando a Fase B' });
      } else if (fase === 'fase_b') {
        // Incluir datos del formulario al completar Fase B
        await tareasDespachoAPI.minutas.updateFaseB(minuta.id, { 
          ...formData,
          importe_total_minuta: formData.importe_total_minuta ? parseFloat(formData.importe_total_minuta) : null,
          fase_b_completada: true 
        });
        toast({ title: 'Fase B completada, avanzando a Fase C' });
      } else if (fase === 'fase_c') {
        await tareasDespachoAPI.minutas.updateFaseC(minuta.id, { fase_c_completada: true });
        toast({ title: 'Minuta finalizada con número asignado' });
      }
      loadTareas();
      setIsDetailOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo avanzar la fase', variant: 'destructive' });
    }
  };

  const Icon = config.icon;

  // Filtrar por fase y completadas
  const [filterCompletadaDef, setFilterCompletadaDef] = useState('pendientes');
  
  const tareasFiltradas = tareas.filter(t => {
    if (filterFase && t.fase_actual !== filterFase) return false;
    if (filterCompletadaDef === 'pendientes' && t.completada_definitivamente) return false;
    if (filterCompletadaDef === 'completadas' && !t.completada_definitivamente) return false;
    return true;
  });

  const countCompletadas = tareas.filter(t => t.completada_definitivamente).length;
  const countPendientes = tareas.filter(t => !t.completada_definitivamente).length;

  return (
    <>
      {/* Header de categoría */}
      <Card className={`${config.lightBg} border-l-4 ${config.borderColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{config.nombre}</h2>
                <p className="text-sm text-gray-600">Workflow de 3 fases: Solicitud → Tramitación → Finalización</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Badge variant="outline" className="text-sm">
                {countPendientes} pendientes
              </Badge>
              <Badge className="bg-green-500 text-white text-sm">
                {countCompletadas} completadas
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Indicadores de fases */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(FASES_MINUTAS).map(([key, fase]) => {
          const count = tareas.filter(t => t.fase_actual === key && !t.completada_definitivamente).length;
          return (
            <Card 
              key={key} 
              className={`cursor-pointer transition-all hover:shadow-md ${filterFase === key ? 'ring-2 ring-green-500' : ''}`}
              onClick={() => setFilterFase(filterFase === key ? '' : key)}
            >
              <CardContent className="p-4 text-center">
                <div className="text-2xl mb-1">{fase.icon}</div>
                <p className="font-medium text-sm">{fase.nombre}</p>
                <p className="text-2xl font-bold text-green-600">{count}</p>
              </CardContent>
            </Card>
          );
        })}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${filterCompletadaDef === 'completadas' ? 'ring-2 ring-green-500 bg-green-50' : ''}`}
          onClick={() => {
            setFilterCompletadaDef(filterCompletadaDef === 'completadas' ? 'pendientes' : 'completadas');
            setFilterFase('');
          }}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl mb-1">✅</div>
            <p className="font-medium text-sm">Completadas</p>
            <p className="text-2xl font-bold text-green-600">{countCompletadas}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros y acciones */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar minutas por número, expediente, procurador..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadTareas()}
                />
              </div>
            </div>
            
            <Select value={filterFase || 'all'} onValueChange={(v) => setFilterFase(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrar por fase" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las fases</SelectItem>
                {Object.entries(FASES_MINUTAS).map(([key, fase]) => (
                  <SelectItem key={key} value={key}>{fase.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterCompletadaDef} onValueChange={(v) => setFilterCompletadaDef(v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="pendientes">Pendientes</SelectItem>
                <SelectItem value="completadas">Completadas</SelectItem>
              </SelectContent>
            </Select>
            
            <Button className={`${config.bgColor} hover:opacity-90 text-white`} onClick={() => setIsDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              NUEVA MINUTA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de minutas */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : tareasFiltradas.length > 0 ? (
            <div className="divide-y">
              {tareasFiltradas.map((minuta) => {
                const faseInfo = FASES_MINUTAS[minuta.fase_actual];
                const isCompleta = minuta.completada_definitivamente;
                
                return (
                  <div 
                    key={minuta.id}
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${isCompleta ? 'bg-green-50' : ''}`}
                    onClick={() => handleViewDetail(minuta)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {isCompleta ? (
                            <Badge className="bg-green-500 text-white">
                              ✅ COMPLETADA - Nº {minuta.numero_minuta}
                            </Badge>
                          ) : (
                            <Badge className={faseInfo?.color}>
                              {faseInfo?.icon} {faseInfo?.nombre}
                            </Badge>
                          )}
                          {minuta.prioridad === 'urgente' && !isCompleta && (
                            <Badge className="bg-red-100 text-red-700 animate-pulse">🚨 Urgente</Badge>
                          )}
                          {minuta.numero_minuta && !isCompleta && (
                            <Badge className="bg-blue-500 text-white">
                              Nº {minuta.numero_minuta}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="font-medium">
                          {minuta.descripcion_tarea || 'Sin descripción'}
                        </p>
                        
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-600">
                          {minuta.expediente_numero && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <FileText className="w-3 h-3" />
                              {minuta.expediente_numero}
                            </span>
                          )}
                          
                          {minuta.procurador_nombre && (
                            <span className="flex items-center gap-1 text-purple-600">
                              <User className="w-3 h-3" />
                              {minuta.procurador_nombre}
                            </span>
                          )}
                          
                          {(minuta.importe_minuta_final || minuta.importe_total_minuta) && (
                            <span className="font-medium text-green-600">
                              €{(minuta.importe_minuta_final || minuta.importe_total_minuta).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                            </span>
                          )}
                          
                          {minuta.fecha_presentacion && (
                            <span className="flex items-center gap-1 text-orange-600">
                              <Calendar className="w-3 h-3" />
                              Presentar: {formatDate(minuta.fecha_presentacion)}
                            </span>
                          )}
                          
                          {minuta.fecha_limite_fase_a && minuta.fase_actual === 'fase_a' && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Límite: {formatDate(minuta.fecha_limite_fase_a)}
                            </span>
                          )}
                          
                          {minuta.email_enviado && (
                            <Badge variant="outline" className="text-xs text-purple-600 border-purple-300">
                              <Mail className="w-3 h-3 mr-1" /> Email enviado
                            </Badge>
                          )}
                          
                          {minuta.actuacion_creada_id && (
                            <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                              <CheckCircle className="w-3 h-3 mr-1" /> Actuación creada
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewDetail(minuta); }} title="Ver detalle">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {(!isCompleta || isAdmin) && (
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewDetail(minuta); }} title="Editar">
                            <Edit className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}
                        {(isAdmin || !isCompleta) && (
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(minuta); }} title="Eliminar" data-testid="delete-minuta-btn">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay minutas</p>
              <Button variant="outline" className="mt-4" onClick={() => setIsDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primera minuta
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nueva Minuta */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              Nueva Minuta (Fase A - Solicitud)
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                <p className="font-medium text-yellow-800">📝 Fase A - Solicitud</p>
                <p className="text-yellow-700">El administrativo preparará el documento según esta solicitud.</p>
              </div>
              
              <div>
                <Label>Expediente relacionado</Label>
                <ExpedienteSearch
                  value={selectedExpediente}
                  onChange={setSelectedExpediente}
                  placeholder="Buscar expediente por número o título..."
                />
              </div>
              
              <div>
                <Label>Descripción de la tarea</Label>
                <Textarea 
                  value={form.descripcion_tarea}
                  onChange={(e) => setForm({...form, descripcion_tarea: e.target.value})}
                  placeholder="Describe lo que debe preparar el administrativo..."
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha límite</Label>
                  <Input 
                    type="date"
                    value={form.fecha_limite_fase_a}
                    onChange={(e) => setForm({...form, fecha_limite_fase_a: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Asignar a</Label>
                  <Select value={form.asignado_a_id} onValueChange={handleUsuarioChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Administrativo" />
                    </SelectTrigger>
                    <SelectContent>
                      {usuarios.map(u => (
                        <SelectItem key={u.id} value={u.id.toString()}>
                          {u.nombre || u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className={`${config.bgColor} hover:opacity-90 text-white`} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Crear Minuta
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle Minuta */}
      <MinutaDetailDialog 
        minuta={selectedMinuta}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onAvanzarFase={handleAvanzarFase}
        usuarios={usuarios}
        loadTareas={loadTareas}
      />
    </>
  );
};


export default TareaMinutasTab;
